// lib/consent/cookie.ts
//
// Single source of truth for the shape of the consent cookie, plus
// isomorphic read/write helpers. layout.tsx (server component) uses the
// server read to decide whether to render the GA / LogRocket <Script>
// tags at all; usePrivacyConsent (client) uses the client read/write to
// hydrate Redux and persist changes.
//
// Bump CONSENT_POLICY_VERSION any time a new tracker is added or a
// category's purpose changes — a version mismatch is treated as "no
// valid consent" and re-triggers the banner, even for returning visitors.

export const CONSENT_COOKIE_NAME = "majikah_consent";
export const CONSENT_POLICY_VERSION = 1;
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180; // 180 days

export type ConsentCategory = "analytics" | "sessionReplay";

export interface ConsentCookieValue {
  essential: true;
  analytics: boolean;
  sessionReplay: boolean;
  policyVersion: number;
  timestamp: string; // ISO string
}

function isValidConsentShape(value: unknown): value is ConsentCookieValue {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    v.essential === true &&
    typeof v.analytics === "boolean" &&
    typeof v.sessionReplay === "boolean" &&
    typeof v.policyVersion === "number" &&
    v.policyVersion === CONSENT_POLICY_VERSION &&
    typeof v.timestamp === "string"
  );
}

function safeParse(raw: string | undefined | null): ConsentCookieValue | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return isValidConsentShape(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Client-side read. Call only in "use client" components/hooks, inside an
 * effect (document.cookie isn't available during SSR).
 */
export function readConsentCookieClient(): ConsentCookieValue | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${CONSENT_COOKIE_NAME}=`));
  if (!match) return null;
  const raw = match.slice(match.indexOf("=") + 1);
  return safeParse(raw);
}

/**
 * Client-side write. Not httpOnly — this only ever holds non-sensitive
 * consent flags, never auth/session data, so plain document.cookie access
 * is fine and avoids an extra round trip through a route handler.
 */
export function writeConsentCookie(value: ConsentCookieValue): void {
  if (typeof document === "undefined") return;
  const payload = encodeURIComponent(JSON.stringify(value));
  document.cookie = [
    `${CONSENT_COOKIE_NAME}=${payload}`,
    "path=/",
    `max-age=${CONSENT_COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ].join("; ");
}

/**
 * Server-side read for use in layout.tsx (App Router server component).
 * Pass the raw cookie value from next/headers' cookies().get(name)?.value.
 */
export function parseConsentCookieServer(
  raw: string | undefined,
): ConsentCookieValue | null {
  return safeParse(raw);
}

export function buildConsentValue(
  partial: Partial<Pick<ConsentCookieValue, "analytics" | "sessionReplay">>,
  previous?: Pick<ConsentCookieValue, "analytics" | "sessionReplay">,
): ConsentCookieValue {
  return {
    essential: true,
    analytics: partial.analytics ?? previous?.analytics ?? false,
    sessionReplay: partial.sessionReplay ?? previous?.sessionReplay ?? false,
    policyVersion: CONSENT_POLICY_VERSION,
    timestamp: new Date().toISOString(),
  };
}
